import {useCallback, useEffect, useState} from 'react'

import {ItemType} from '../../api/memex-items/item-type'
import {not_typesafe_nonNullAssertion} from '../../helpers/non-null-assertion'
import {isPlatformMeta} from '../../helpers/util'
import {
  getVisibleCells,
  rowGetCanSelect,
  rowIsGrouped,
  rowIsSelected,
  rowToggleSelected,
} from '../../hooks/tanstack-table/helpers/row-api'
import type {Row} from '../../hooks/tanstack-table/helpers/types'
import {useSetRowSelection, useTableSelectedFlatRows} from '../../hooks/tanstack-table/hooks/use-table-row-selection'
import {useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import {Direction} from '../../selection/types'
import {focusCell, focusOmnibar, type TableFocusState, useStableTableNavigation} from './navigation'
import {useTableCellBulkSelectionActions} from './table-cell-bulk-selection'
import type {TableDataType} from './table-data-type'

export const isRowActionable = (row: Row<TableDataType>) => row.original.contentType !== ItemType.RedactedItem

export const isRowSelectedAndActionable = (row?: Row<TableDataType>) =>
  row !== undefined && rowIsSelected(row) && isRowActionable(row)

/** Rows in collapsed groups, parent rows, and redacted items aren't selectable */
export const isRowSelectable = (row: Row<TableDataType>) =>
  isRowActionable(row) && (row.parentId ? row.getIsAllParentsExpanded() : !row.getIsGrouped())

export const getSelectionInfo = (row: Row<TableDataType>, flatRows: Array<Row<TableDataType>>) => {
  const isSelected = isRowSelectedAndActionable(row)
  let beforeSelected = false
  let afterSelected = false
  let firstInGroup = false
  let lastInGroup = false
  if (isSelected) {
    const rowsWithoutGroups = flatRows.filter(r => !r.getIsGrouped())
    const rowIndex = rowsWithoutGroups.findIndex(r => r.id === row.id)
    beforeSelected = isRowSelectedAndActionable(rowsWithoutGroups[rowIndex - 1])
    afterSelected = isRowSelectedAndActionable(rowsWithoutGroups[rowIndex + 1])

    const parentRow = row.getParentRow()
    const parentIsGrouped = parentRow?.getIsGrouped()
    firstInGroup = !!(parentIsGrouped && parentRow?.subRows[0]?.id === row.id)
    lastInGroup = !!(parentIsGrouped && parentRow?.subRows.slice(-1)[0]?.id === row.id)
  }

  return {beforeSelected, afterSelected, firstInGroup, lastInGroup}
}

const defaultClickTargets: Array<React.RefObject<HTMLElement | null>> = []
export function useRowSelection(
  row: Row<TableDataType>,
  clickTargets: Array<React.RefObject<HTMLElement | null>> = defaultClickTargets,
) {
  const {navigationDispatch, stateRef: focusStateRef} = useStableTableNavigation()
  const table = useTableInstance()
  const flatRows = table.getRowModel().flatRows
  const selectedFlatRows = useTableSelectedFlatRows()
  const [lastFocus, setLastFocus] = useState<TableFocusState | null>(null)
  const [lastFocusIndex, setLastFocusIndex] = useState<number | null>(null)
  const bulkSelectActions = useTableCellBulkSelectionActions()
  const setRowSelection = useSetRowSelection()

  useEffect(() => {
    // Clear cell selections when any rows get selected
    if (selectedFlatRows.length > 0) bulkSelectActions?.clearSelection()
  }, [selectedFlatRows, bulkSelectActions])

  const expandRowSelection = useCallback(
    (anchorId: string) => {
      const selectableRows = flatRows.filter(r => rowGetCanSelect(r))
      const anchorRowIndex = selectableRows.findIndex(r => r.id === anchorId)
      const rowIndex = selectableRows.findIndex(r => r.id === row.id)
      let rowsToSelect: Array<Row<TableDataType>> = []
      if (rowIndex < anchorRowIndex) {
        rowsToSelect = selectableRows.slice(rowIndex, anchorRowIndex + 1)
      } else if (rowIndex > anchorRowIndex) {
        rowsToSelect = selectableRows.slice(anchorRowIndex, rowIndex + 1)
      }
      if (rowsToSelect.length) {
        setRowSelection(rowsToSelect.map(r => r.id))
      }
    },
    [flatRows, row.id, setRowSelection],
  )

  const expandRowSelectionInDirection = useCallback(
    (direction: Direction, focusedRow: Row<TableDataType>) => {
      const selectedRows = selectedFlatRows.filter(r => !rowIsGrouped(r))
      if (selectedRows.length === 0) return

      const selectableRows = flatRows.filter(r => rowGetCanSelect(r))
      let rowsToSelect: Array<Row<TableDataType>> = []

      const startRow = not_typesafe_nonNullAssertion(selectedRows[0])
      const startIndex = selectableRows.findIndex(r => r.id === startRow.id)
      const endRow = not_typesafe_nonNullAssertion(selectedRows[selectedRows.length - 1])
      const endIndex = selectableRows.findIndex(r => r.id === endRow.id)
      const focusedRowIndex = selectableRows.findIndex(r => r.id === focusedRow.id)

      if (selectedRows.length === 1) {
        // focused row <- start index, end index
        if (direction === Direction.Up) {
          rowsToSelect = selectableRows.slice(startIndex - 1, startIndex + 1)
        } else if (direction === Direction.Down) {
          rowsToSelect = selectableRows.slice(startIndex, startIndex + 2)
        }
      } else if (focusedRowIndex < endIndex) {
        // focused row <- start index
        // selected row
        // selected row <- end index
        if (direction === Direction.Up) {
          rowsToSelect = selectableRows.slice(startIndex, endIndex)
        } else if (direction === Direction.Down) {
          rowsToSelect = selectableRows.slice(startIndex, endIndex + 2)
        }
      } else {
        // selected row <- start index
        // selected row
        // focused row <- end index
        if (direction === Direction.Up) {
          if (startIndex === 0) return
          rowsToSelect = selectableRows.slice(startIndex - 1, focusedRowIndex + 1)
        } else if (direction === Direction.Down) {
          rowsToSelect = selectableRows.slice(startIndex + 1, focusedRowIndex + 1)
        }
      }

      if (rowsToSelect.length) {
        setRowSelection(rowsToSelect.map(r => r.id))
      }
    },
    [selectedFlatRows, flatRows, setRowSelection],
  )

  const onRowClick = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    (e: React.MouseEvent | PointerEvent) => {
      if (!clickTargets.some(i => i.current === e.target)) return

      if (isPlatformMeta(e)) {
        rowToggleSelected(row)
        const nextFocusCell = getVisibleCells(row)[1]
        if (nextFocusCell) {
          navigationDispatch(focusCell(row.id, nextFocusCell.column.id, false))
        }
      } else if (selectedFlatRows.length && e.shiftKey && lastFocus?.focus?.type === 'coordinate') {
        const {x: columnId, y: rowId} = lastFocus.focus.details
        expandRowSelection(rowId)
        navigationDispatch(focusCell(rowId, columnId, false))
      } else {
        setRowSelection([row.id])
        const nextFocusCell = getVisibleCells(row)[1]
        if (nextFocusCell) {
          navigationDispatch(focusCell(row.id, nextFocusCell.column.id, false))
        }
      }
    },
    [
      clickTargets,
      selectedFlatRows.length,
      lastFocus?.focus?.type,
      lastFocus?.focus?.details,
      row,
      navigationDispatch,
      expandRowSelection,
      setRowSelection,
    ],
  )

  const onRowPointerDown = useCallback(() => {
    if (focusStateRef.current?.focus?.type === 'coordinate') {
      setLastFocus(focusStateRef.current)
      const {y: rowId} = focusStateRef.current.focus.details
      const lastIndex = flatRows.findIndex(r => r.id === rowId)
      setLastFocusIndex(lastIndex)
    } else {
      // If clicking directly on row action menu with no coordinate selection, mark
      // last focus index as current row to refocus after menu closes
      setLastFocusIndex(flatRows.findIndex(r => r.id === row.id))
    }
  }, [flatRows, focusStateRef, row])

  const focusLastRowIndex = useCallback(
    (removedItemIds: Array<number>) => {
      if (lastFocusIndex !== null && lastFocusIndex > -1) {
        // Exclude items that have been removed/archived from the table
        const removedItemIdsSet = new Set(removedItemIds)
        const remainingRows = flatRows.filter(
          r => !removedItemIdsSet.has(r.original.id) && getVisibleCells(r).length > 1,
        )
        const lastIndex = remainingRows.length - 1
        const nextFocusIndex = lastFocusIndex > lastIndex ? lastIndex : lastFocusIndex
        // Get the new row at the same index as the last focused row
        const focusRow = remainingRows[nextFocusIndex]

        if (focusRow) {
          const nextFocusCell = getVisibleCells(focusRow)[1]
          if (nextFocusCell) {
            navigationDispatch(focusCell(focusRow.id, nextFocusCell.column.id, false))
          }
        } else {
          navigationDispatch(focusOmnibar())
        }
      }
    },
    [flatRows, lastFocusIndex, navigationDispatch],
  )

  return {onRowClick, onRowPointerDown, focusLastRowIndex, expandRowSelectionInDirection}
}
